import * as React from "react";
import { AlertCircle } from "lucide-react";
import { Button, Label, cn } from "@eduviaos/ui-kit";

export function ErrorBanner({
  message,
  onRetry,
  className,
}: {
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
        className,
      )}
    >
      <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <span className="flex-1">{message}</span>
      {onRetry ? (
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  required,
  helper,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {helper ? <p className="text-muted-foreground mt-1 text-xs">{helper}</p> : null}
    </div>
  );
}

/** Native select styled like the toolbar selects used across IA. */
export function NativeSelect({
  className,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cn(
        "border-input bg-background focus-visible:ring-ring h-9 w-full rounded-md border px-2 text-sm focus-visible:outline-none focus-visible:ring-1",
        className,
      )}
    />
  );
}

/** Calm summary sentence box used for allocation and EMI previews. */
export function PreviewNote({
  children,
  tone = "neutral",
  busy,
}: {
  children: React.ReactNode;
  tone?: "neutral" | "advance";
  busy?: boolean;
}) {
  return (
    <div
      aria-live="polite"
      className={cn(
        "rounded-lg border px-3.5 py-3 text-sm leading-relaxed transition-opacity duration-150 ease-out",
        tone === "advance"
          ? "border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20"
          : "border-border/70 bg-muted/30",
        busy && "opacity-60",
      )}
    >
      {children}
    </div>
  );
}
